import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Row,
  Col,
  Input,
  Table,
  Card,
  CardBody,
  Badge
} from 'reactstrap';
import { useListOpportunities } from 'views/CRM/Opportunity/hooks/useListOpportunities';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import ReactLoader from 'components/Loader';
import RecordTypeSelection from 'views/RecordType/components/RecordTypeSelection';
import Pagination from 'components/Pagination';
import { FaCheckCircle } from 'react-icons/fa';

import { toast } from 'react-toastify';

export const OpportunitiesSelection = ({
  conversation, // eslint-disable-line no-unused-vars
  onClose,
  onAdvance,
  selectedOpportunities: initialSelectedOpportunities = [],
  associations = null
}) => {
  const [selectedRecordTypeId, setSelectedRecordTypeId] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(
    initialSelectedOpportunities.length > 0
      ? initialSelectedOpportunities[0]
      : null
  );
  const [activePage, setActivePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const { recordTypes, isLoadingRecordTypes, getRecordTypesByUser } =
    useRecordTypesByUser(entitiesName.OPPORTUNITY);

  const {
    opportunities,
    totalOpportunities,
    isLoadingOpportunities,
    getOpportunities
  } = useListOpportunities({
    recordTypeId: selectedRecordTypeId,
    sortConfig: { key: null, direction: null }
  });

  useEffect(() => {
    getRecordTypesByUser();
  }, [getRecordTypesByUser]);

  useEffect(() => {
    if (recordTypes && recordTypes.length > 0 && !selectedRecordTypeId) {
      setSelectedRecordTypeId(recordTypes[0].id);
    }
  }, [recordTypes, selectedRecordTypeId]);

  // Debounce do termo de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset da página quando o termo de busca muda
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setActivePage(1);
    }
  }, [debouncedSearchTerm, searchTerm]);

  useEffect(() => {
    if (selectedRecordTypeId) {
      handleSearch();
    }
  }, [selectedRecordTypeId, activePage, debouncedSearchTerm]);

  const handleSearch = useCallback(() => {
    if (selectedRecordTypeId) {
      const filters = debouncedSearchTerm
        ? { textQuery: debouncedSearchTerm }
        : {};
      getOpportunities(activePage, filters);
    }
  }, [selectedRecordTypeId, activePage, debouncedSearchTerm, getOpportunities]);

  const handleOnSelectedRecordType = useCallback((recordTypeId) => {
    setSelectedRecordTypeId(recordTypeId);
    setActivePage(1);
    setSelectedOpportunity(null);
  }, []);

  // Função para verificar se uma oportunidade já está associada
  const isOpportunityAssociated = useCallback(
    (opportunityId) => {
      if (!associations?.data?.opportunityStatus) return false;
      const opportunityStatus = associations.data.opportunityStatus.find(
        (status) => status.opportunityId === opportunityId
      );
      return opportunityStatus?.isAssociated || false;
    },
    [associations]
  );

  const handleOpportunitySelection = (opportunityId) => {
    // Não permitir selecionar oportunidades já associadas
    if (isOpportunityAssociated(opportunityId)) {
      toast.warning('Esta oportunidade já está associada a esta conversa');
      return;
    }
    setSelectedOpportunity(opportunityId);
  };

  const handleAdvance = () => {
    if (!selectedOpportunity) {
      toast.warning('Selecione uma oportunidade para avançar');
      return;
    }
    const selectedOpportunityData = opportunities?.find(
      (opportunity) => opportunity.id === selectedOpportunity
    );
    onAdvance(
      [selectedOpportunity],
      selectedRecordTypeId,
      selectedOpportunityData ? [selectedOpportunityData] : []
    );
  };

  // Função para formatar valor monetário
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (isLoadingRecordTypes) {
    return (
      <Row className="justify-content-md-center">
        <ReactLoader />
      </Row>
    );
  }

  if (!selectedRecordTypeId) {
    return (
      <RecordTypeSelection
        recordTypes={recordTypes}
        handleOnSelectedRecordType={handleOnSelectedRecordType}
      />
    );
  }

  // Componente responsivo para exibir oportunidades
  const OpportunityItem = ({ opportunity, isSelected, onSelect }) => {
    const isAssociated = isOpportunityAssociated(opportunity.id);
    const itemStyle = isAssociated
      ? { backgroundColor: '#f8f9fa', opacity: 0.7 }
      : {};

    return (
      <>
        {/* Versão Desktop - Tabela */}
        <tr className="d-none d-md-table-row" style={itemStyle}>
          <td
            className="text-center align-middle"
            style={{
              verticalAlign: 'middle !important',
              padding: '0.75rem',
              width: '50px'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}
            >
              {isAssociated ? (
                <FaCheckCircle
                  size={20}
                  color="#28a745"
                  title="Oportunidade já associada"
                />
              ) : (
                <div
                  onClick={() => onSelect(opportunity.id)}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: '2px solid #007bff',
                    backgroundColor: isSelected ? '#007bff' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSelected && (
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'white'
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {opportunity.title || opportunity.name || '-'}
            {isAssociated && (
              <Badge color="success" className="ms-2" size="sm">
                Associada
              </Badge>
            )}
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {opportunity.account?.name || '-'}
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {formatCurrency(opportunity.amount)}
          </td>
          <td
            className="align-middle"
            style={{ verticalAlign: 'middle', padding: '0.75rem' }}
          >
            {opportunity.opportunityStage?.name || opportunity.stageName || '-'}
          </td>
        </tr>

        {/* Versão Mobile - Card */}
        <div className="d-block d-md-none mb-2">
          <Card style={itemStyle}>
            <CardBody className="p-3">
              <Row className="align-items-center">
                <Col xs="2">
                  {isAssociated ? (
                    <FaCheckCircle
                      size={20}
                      color="#28a745"
                      title="Oportunidade já associada"
                    />
                  ) : (
                    <div
                      onClick={() => onSelect(opportunity.id)}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: '2px solid #007bff',
                        backgroundColor: isSelected ? '#007bff' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isSelected && (
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'white'
                          }}
                        />
                      )}
                    </div>
                  )}
                </Col>
                <Col xs="10">
                  <div>
                    <strong>
                      {opportunity.title ||
                        opportunity.name ||
                        'Título não informado'}
                    </strong>
                    {isAssociated && (
                      <Badge color="success" className="ms-2" size="sm">
                        Associada
                      </Badge>
                    )}
                  </div>
                  {opportunity.account?.name && (
                    <div className="text-muted small">
                      🏢 {opportunity.account.name}
                    </div>
                  )}
                  {opportunity.amount && (
                    <div className="text-muted small">
                      💰 {formatCurrency(opportunity.amount)}
                    </div>
                  )}
                  {(opportunity.opportunityStage?.name ||
                    opportunity.stageName) && (
                    <div className="text-muted small">
                      🎯{' '}
                      {opportunity.opportunityStage?.name ||
                        opportunity.stageName}
                    </div>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </div>
      </>
    );
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md={12}>
          <Input
            type="text"
            placeholder="Buscar oportunidades por nome, conta ou valor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && searchTerm !== debouncedSearchTerm && (
            <small className="text-muted">Buscando...</small>
          )}
        </Col>
      </Row>

      {isLoadingOpportunities ? (
        <Row className="justify-content-md-center">
          <ReactLoader />
        </Row>
      ) : (
        <>
          {/* Cabeçalho informativo */}
          <div className="mb-3">
            <Row>
              <Col>
                <small className="text-muted">
                  Selecione uma oportunidade para continuar (
                  {opportunities?.length || 0} oportunidades disponíveis)
                </small>
              </Col>
            </Row>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {/* Tabela Desktop */}
            <Table
              responsive
              striped
              className="d-none d-md-table align-items-center"
            >
              <thead>
                <tr>
                  <th style={{ width: '50px' }} className="text-center">
                    Seleção
                  </th>
                  <th>Título</th>
                  <th>Conta</th>
                  <th>Valor</th>
                  <th>Fase</th>
                </tr>
              </thead>
              <tbody>
                {opportunities?.length > 0 ? (
                  opportunities.map((opportunity) => (
                    <OpportunityItem
                      key={opportunity.id}
                      opportunity={opportunity}
                      isSelected={selectedOpportunity === opportunity.id}
                      onSelect={handleOpportunitySelection}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      Nenhuma oportunidade encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {/* Cards Mobile */}
            <div className="d-block d-md-none">
              {opportunities?.length > 0 ? (
                opportunities.map((opportunity) => (
                  <OpportunityItem
                    key={opportunity.id}
                    opportunity={opportunity}
                    isSelected={selectedOpportunity === opportunity.id}
                    onSelect={handleOpportunitySelection}
                  />
                ))
              ) : (
                <Card>
                  <CardBody className="text-center text-muted">
                    Nenhuma oportunidade encontrada
                  </CardBody>
                </Card>
              )}
            </div>
          </div>

          {opportunities?.length > 0 && (
            <Row className="mt-3">
              <Col md={12} className="d-flex justify-content-center">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={10}
                  totalItemsCount={totalOpportunities}
                  pageRangeDisplayed={3}
                  onChange={setActivePage}
                />
              </Col>
            </Row>
          )}

          <Row className="mt-3">
            <Col xs={12} md={6} className="mb-2 mb-md-0">
              <span className="text-muted">
                {selectedOpportunity
                  ? `Oportunidade selecionada: ${
                      opportunities?.find(
                        (opportunity) => opportunity.id === selectedOpportunity
                      )?.title ||
                      opportunities?.find(
                        (opportunity) => opportunity.id === selectedOpportunity
                      )?.name ||
                      'Título não informado'
                    }`
                  : 'Nenhuma oportunidade selecionada'}
              </span>
            </Col>
            <Col xs={12} md={6} className="d-flex justify-content-end">
              <Button
                color="secondary"
                size="sm"
                className="me-2"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={handleAdvance}
                disabled={!selectedOpportunity}
              >
                Avançar
              </Button>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};
